import { getMeta } from "app/utils/meta";
import { getLoaderTrans } from "app/.server/utils";
import InputField from "app/components/form/input-field";
import PhoneField from "app/components/form/phone-field";
import Layout from "app/components/layout";
import Sprite from "app/components/sprite";
import {
  ZBNI_CCOMMERCE_API_ENDPOINT,
  ZBNI_RECAPTCHA_SITE_KEY,
} from "app/config/constants";
import { useI18nLanguage } from "app/hooks/i18n";
import cardImages from "app/images/ccommerce-report/card-images.png";
import CardBgHalfCircleSm from "app/images/svgs/ccommerce-report/half-circle-sm.svg";
import CardBgHalfCircle from "app/images/svgs/ccommerce-report/half-circle.svg";
import CardBgTriangleSm from "app/images/svgs/ccommerce-report/triangle-sm.svg";
import CardBgTriangle from "app/images/svgs/ccommerce-report/triangle.svg";
import CardBgZigzagSm from "app/images/svgs/ccommerce-report/zigzag-sm.svg";
import CardBgZigzag from "app/images/svgs/ccommerce-report/zigzag.svg";
import { getAccessToken } from "app/utils/access-token";
import { zapierClient } from "app/utils/axios";
import { verifyCaptcha } from "app/utils/captcha";
import classNames from "classnames";
import { Form, Formik } from "formik";
import { useRef, useState } from "react";
import {
  GoogleReCaptchaProvider,
  useGoogleReCaptcha,
} from "react-google-recaptcha-v3";
import { Trans, useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import * as yup from "yup";
import "app/utils/schema/phone";

const SuccessScreen = () => (
  <div className="flex flex-col items-center justify-center text-center gap-6 md:gap-9 px-0 lg:px-5 py-11">
    <Sprite name="zbooni-wink-smile" className="h-20 w-28" />
    <div>
      <p className="max-w-xs md:max-w-[450px] font-black text-[22px] md:text-3xl leading-normal text-center">
        <Trans>Thank you for submitting the form!</Trans>
      </p>
    </div>
    <a
      id="cta-ccommerce-report-download"
      href="https://media.zbooni.shop/marketing/reports/the-mena-ccommerce-report-2024.pdf"
      target="_blank"
      rel="noopener noreferrer"
      className="h-auto xs:h-12 w-full xs:w-72 rounded-full bg-green-darkest hover:opacity-80 py-2 px-3 flex justify-center items-center text-primary text-base leading-normal font-bold"
    >
      <Trans>Download full report</Trans>
    </a>
  </div>
);

export const loader = async ({ request, params }) => {
  const trans = await getLoaderTrans({ request, params });

  return {
    meta: {
      title: trans(
        "Unlock the potential of WhatsApp: Zbooni's cCommerce Report 2024 Reveals Strategies & Insights"
      ),
      description: trans(
        "Dive deep into MENA eCommerce trends & the rise of WhatsApp. Download Zbooni's MENA cCommerce Report 2024 and start growing your business today!"
      ),
    },
  };
};

export const meta = ({ loaderData: data }) => {
  return getMeta({
    title: data.meta.title,
    description: data.meta.description,
    image:
      "https://media.zbooni.shop/assets/web/ccommerce-reports/2024/og_img.png",
  });
};

const CcommerceReportForm = () => {
  const { t: trans } = useTranslation();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [isLoading, setIsLoading] = useState(false);
  const [successPage, showSuccessPage] = useState(false);
  const scrollRef = useRef(null);

  const validationSchema = yup.object().shape({
    name: yup.string().trim().required(trans("Please enter your full name.")),
    email: yup
      .string()
      .email(trans("Enter a valid email address"))
      .required(trans("Please enter your email address.")),
    phone_number: yup.string().phone(trans("Mobile number must be valid.")),
    store_name: yup
      .string()
      .trim()
      .required(trans("Please enter your business name.")),
  });

  const handleSubmit = async (values) => {
    setIsLoading(true);

    if (!executeRecaptcha) {
      toast.error("reCaptcha not yet available. Please try again.");
      setIsLoading(false);
      return;
    }

    const captchaToken = await executeRecaptcha("ccommerce_report_form");
    const accessToken = await getAccessToken();
    const captchaRes = await verifyCaptcha(captchaToken, accessToken);

    if (!captchaRes?.success) {
      toast.error("An error has occurred. Please try again.");
      setIsLoading(false);
      return;
    }

    zapierClient
      .post(ZBNI_CCOMMERCE_API_ENDPOINT, values)
      .then(() => {
        setIsLoading(false);
        showSuccessPage(true);
        scrollRef.current.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      })
      .catch((err) => {
        toast.error(err.message);
        setIsLoading(false);
      });
  };

  return (
    <Layout headerIsOnTop={false} enableHeaderShrinkingOnScroll={false}>
      <div className="relative overflow-hidden bg-ghostwhite">
        <div className="zbooni-layout absolute h-full w-full ltr:left-1/2 ltr:-translate-x-1/2 rtl:right-1/2 rtl:translate-x-1/2">
          <Sprite
            name="pricing-donut"
            className="absolute -bottom-48 h-[500px] w-[500px] ltr:-right-40 rtl:-left-40 md:-bottom-64"
          />
        </div>
        <div className="absolute h-72 w-full bg-primary md:h-96">
          <div className="zbooni-layout relative h-full w-full">
            <Sprite
              className="absolute top-14 h-[200px] w-[400px] opacity-[8%] brightness-[300%] ltr:-left-16 rtl:-right-16 rtl:scale-x-[-1] md:h-[316px] md:w-[680px] ltr:md:-left-32 rtl:md:-right-32"
              name="zigzag-to-top-right"
            />
          </div>
        </div>
        <div
          className={classNames(
            "zbooni-layout relative flex flex-col gap-y-7 md:py-32 lg:flex-row",
            successPage
              ? "md:items-center md:justify-center pt-20 pb-5"
              : "py-24"
          )}
        >
          <div
            className={classNames(
              !successPage
                ? "flex w-full flex-col gap-5 md:gap-8 lg:w-2/5"
                : "hidden"
            )}
          >
            <div className="flex flex-col gap-y-3 max-w-[330px] md:max-w-[400px]">
              <p
                ref={scrollRef}
                className="text-white font-medium text-lg md:text-xl leading-normal"
              >
                <Trans>The MENA</Trans>
                <br />
                <Trans>cCommerce Report 2024</Trans>
              </p>
              <p className="text-white font-extrabold text-xl md:text-2xl leading-normal">
                <Trans>How WhatsApp is changing the way we shop</Trans>
              </p>
            </div>
            <div className="flex items-center justify-center max-w-[326px] lg:max-w-[426px] h-[240px] lg:h-[345px] bg-cyan rounded-2xl overflow-hidden">
              <div className="relative w-[283px] h-[274px] hidden lg:block">
                <div className="absolute z-0 -top-4 -left-10">
                  <img src={CardBgZigzag} alt="" />
                </div>
                <div className="absolute z-0 -bottom-8 -right-[68px]">
                  <img src={CardBgHalfCircle} alt="" />
                </div>
                <div className="absolute z-0 top-0 -right-[42px]">
                  <img src={CardBgTriangle} alt="" />
                </div>
                <img
                  src={cardImages}
                  className="absolute z-10 w-full h-full"
                  alt=""
                />
              </div>
              <div className="relative w-[202px] h-[196px] block lg:hidden">
                <div className="absolute z-0 -top-2.5 -left-7">
                  <img src={CardBgZigzagSm} alt="" />
                </div>
                <div className="absolute z-0 -bottom-5 -right-[50px]">
                  <img src={CardBgHalfCircleSm} alt="" />
                </div>
                <div className="absolute z-0 top-3 -right-[30px]">
                  <img src={CardBgTriangleSm} alt="" />
                </div>
                <img
                  src={cardImages}
                  className="absolute z-10 w-full h-full"
                  alt=""
                />
              </div>
            </div>
          </div>

          <div
            className={classNames(
              "relative flex-1 ltr:lg:ml-7 rtl:lg:mr-7 xl:ml-0",
              successPage
                ? "w-full md:max-w-[720px] h-[415px] md:h-[500px]"
                : ""
            )}
          >
            {isLoading && (
              <div className="absolute h-full w-full rounded-3xl bg-white opacity-50 z-10" />
            )}
            <div
              className={classNames(
                "rounded-3xl bg-white",
                successPage
                  ? "px-5 py-7 lg:py-14 lg:px-12"
                  : "p-4 md:px-12 md:py-6"
              )}
            >
              {successPage ? (
                <SuccessScreen />
              ) : (
                <Formik
                  initialValues={{
                    name: "",
                    email: "",
                    phone_number: "",
                    store_name: "",
                  }}
                  onSubmit={handleSubmit}
                  validationSchema={validationSchema}
                >
                  {({ isSubmitting }) => (
                    <Form>
                      <fieldset disabled={isSubmitting || isLoading}>
                        <legend className="text-primary text-lg leading-5 font-extrabold pb-5 md:pb-7">
                          <Trans>Submit the form to receive the report</Trans>
                        </legend>
                        <div className="grid grid-cols-1 gap-6">
                          <InputField
                            id="name"
                            name="name"
                            type="text"
                            label={trans("Your Name")}
                            placeholder={trans("Your full name")}
                          />
                          <InputField
                            id="email"
                            name="email"
                            type="email"
                            label={trans("Your Email")}
                            placeholder={trans("A valid email address")}
                          />
                          <PhoneField
                            id="phone_number"
                            label={trans("WhatsApp Contact Number")}
                            name="phone_number"
                            defaultCountry="AE"
                          />
                          <InputField
                            id="store_name"
                            name="store_name"
                            type="text"
                            label={trans("Business Name")}
                            placeholder={trans("Your business name")}
                          />
                        </div>
                        <div className="grid grid-cols-1 gap-7 md:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 pt-7 md:pt-5 lg:pt-7 xl:pt-5">
                          <button
                            type="submit"
                            className={classNames(
                              "col-start-1 h-12 w-full rounded-full bg-primary hover:opacity-80 font-bold text-white md:col-start-2 lg:col-start-1 xl:col-start-2",
                              isLoading || isSubmitting
                                ? "bg-primary-light"
                                : ""
                            )}
                          >
                            <Trans>Submit</Trans>
                          </button>
                        </div>
                      </fieldset>
                    </Form>
                  )}
                </Formik>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default function CcommerceReportPage() {
  const { locale: language } = useI18nLanguage();

  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={ZBNI_RECAPTCHA_SITE_KEY}
      language={language}
    >
      <CcommerceReportForm />
    </GoogleReCaptchaProvider>
  );
}
